import { tryCatch } from '@whistler-ui/k2/helpers';

const DOCS_ASSETS_INDEX_PATH = '/docs/assets-index.json';

/** Query param the Developers page reads to reveal and scroll to a figure. */
export const DOCS_ASSET_PARAM = 'asset';

/**
 * Asset name without its extension. The production build rewrites every
 * `<name>.png` occurrence in the bundle to `<name>.webp` (image-to-webp
 * plugin), including string literals and glob keys, so client code must only
 * ever compare stems; the extension is not stable between dev and prod.
 */
export const getDocsAssetStem = (name: string): string => {
  return name.replace(/\.[a-z0-9]+$/i, '');
};

/** `{ "<asset basename>": "/developers?tab=<tab>" }`, published by the docs extractor. */
export type DocsAssetsIndex = Record<string, string>;

export const getDocsAssetsIndex = async (): Promise<DocsAssetsIndex> => {
  const { data, error } = await tryCatch<DocsAssetsIndex>(async () => {
    const response = await fetch(DOCS_ASSETS_INDEX_PATH);

    if (!response.ok) {
      throw new Error(
        `Failed to load ${DOCS_ASSETS_INDEX_PATH} (${response.status} ${response.statusText})`,
      );
    }

    return (await response.json()) as DocsAssetsIndex;
  });

  if (error) {
    throw error;
  }

  return data ?? {};
};

/**
 * Documentation link for an image the doc assistant echoed back, or null
 * when it is not a published docs asset: the tab route from the index plus
 * `?asset=<basename>` so the page can reveal the figure. Accepts the
 * root-relative `/docs/assets/x.png` the extractor writes and its absolute
 * same-origin form; anything outside that folder is not ours to link.
 */
export const getDocsAssetRoute = (
  index: DocsAssetsIndex | undefined,
  src: string | undefined,
): string | null => {
  if (!index || !src) {
    return null;
  }

  const pathname = src.replace(/^[a-z]+:\/\/[^/]+/i, '').split(/[?#]/)[0];
  const match = pathname.match(/^\/docs\/assets\/([^/]+)$/);

  if (!match) {
    return null;
  }

  const route = index[match[1]];

  if (!route) {
    return null;
  }

  return `${route}&${DOCS_ASSET_PARAM}=${encodeURIComponent(match[1])}`;
};
